import React from 'react';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import { useFeatureFlags } from '@shared/flags/FeatureFlagProvider';

export interface IonicRouteViewProps {
  children: React.ReactNode;
}

/**
 * Lightweight Ionic route wrapper for React Router-based apps.
 *
 * Note: We intentionally do NOT use IonPage/IonContent here.
 * Those components are designed for Ionic's own IonRouterOutlet navigation
 * system and use shadow DOM + CSS containment (position: absolute, overflow:
 * hidden) that breaks:
 *   1. Playwright visibility checks (elements report as "hidden")
 *   2. Standard DOM scrolling behavior
 *   3. Third-party component library layout assumptions
 *
 * Instead we use a plain wrapper div. The IonApp at the top level (via
 * IonicShell) is sufficient for Capacitor compatibility, safe-area insets,
 * and Ionic theming. When/if we migrate to IonRouterOutlet navigation,
 * IonPage/IonContent should be re-introduced at that point.
 */
const IonicRouteView: React.FC<IonicRouteViewProps> = ({ children }) => {
  const { flags } = useFeatureFlags();
  const { isNative } = useCapacitorPlatform();
  const forceWebLayout = import.meta.env.VITE_IONIC_WEB_LAYOUT === '1';
  const shouldWrap = flags.ionic_enabled && (isNative || forceWebLayout);

  if (!shouldWrap) {
    return <>{children}</>;
  }

  return (
    <div className="ionic-route-view" style={{ minHeight: '100%' }}>
      {children}
    </div>
  );
};

export default IonicRouteView;
